import {useState, type FC} from 'react'
import type {Ruleset, SourceType} from '../types/rules-types'
import {RulesetEnforcement} from '../types/rules-types'
import {AlertIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionMenu, IconButton} from '@primer/react'

import {PLURAL_RULESET_TARGETS} from '../helpers/constants'
import {capitalize, humanize, pluralize} from '../helpers/string'
import {Link} from '@github-ui/react-core/link'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {EnforcementIcon} from './EnforcementIcon'
import {deleteRuleset, InsightsLink, RulesetActionMenu} from './RulesetActionMenu'
import {useIsStafftools} from '../hooks/use-is-stafftools'
import type {FlashAlert} from '@github-ui/dismissible-flash'

import {useTargetCount} from '../hooks/use-target-count'
import type {Repository} from '@github-ui/current-repository'
import type {Organization} from '@github-ui/repos-types'
import {useRuleFeatures} from '../hooks/use-rule-features'
import {useRuleStrings} from '../hooks/use-rule-strings'
import {DangerConfirmationDialog} from './ruleset/DangerConfirmationDialog'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const RulesetListHelper: FC<{
  ruleset: Ruleset
  sourceType: SourceType
  readOnly?: boolean
  setFlashAlert: (flashAlert: FlashAlert) => void
  rulesetTargetCount: number | undefined
}> = ({ruleset, sourceType, readOnly, setFlashAlert, rulesetTargetCount}) => {
  const {historyEnabled, importExportEnabled} = useRuleFeatures()
  const rulesHistory = historyEnabled
  const rulesImportExport = importExportEnabled
  const {search, resolvePath} = useRelativeNavigation()
  const key = ruleset.source.type
  const isStafftools = useIsStafftools()
  const [menuOpen, setMenuOpen] = useState(false)
  const {rulesetOrPolicy} = useRuleStrings()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const {navigate} = useRelativeNavigation()
  const rulesetDeletionConfirmation = isFeatureEnabled('ruleset_deletion_confirmation')

  return (
    <>
      {(rulesHistory || rulesImportExport) && (
        <div className="align-center mr-2">
          <EnforcementIcon hideText enforcement={ruleset.enforcement} />
        </div>
      )}
      <div className="d-flex flex-column">
        <div className="f5 text-bold">
          <Link className="color-fg-default wb-break-word" to={{pathname: resolvePath(`${ruleset.id}`), search}}>
            {ruleset.name}
          </Link>
        </div>
        {rulesHistory || rulesImportExport ? (
          <div className="d-flex flex-row">
            <span className="text-small color-fg-muted mr-1">
              <Subtext ruleset={ruleset} sourceType={sourceType} targetCount={rulesetTargetCount} />
            </span>
          </div>
        ) : (
          <div className="d-flex flex-row my-1 text-small color-fg-muted flex-items-center gap-1">
            <EnforcementIcon enforcement={ruleset.enforcement} />
            <Subtext ruleset={ruleset} sourceType={sourceType} targetCount={rulesetTargetCount} />
          </div>
        )}
        <MissingConditionAlert ruleset={ruleset} />
      </div>
      {(rulesHistory || rulesImportExport) && (isStafftools || !readOnly) && key === 'Repository' ? (
        <div className="ml-auto d-flex flex-items-center">
          <ActionMenu open={menuOpen} onOpenChange={() => setMenuOpen(!menuOpen)}>
            <ActionMenu.Anchor>
              <IconButton
                icon={KebabHorizontalIcon}
                size="small"
                variant="invisible"
                className="color-fg-muted align-center"
                aria-label={`${capitalize(rulesetOrPolicy)} menu`}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <RulesetActionMenu
                ruleset={ruleset}
                rulesetsUrl={resolvePath('')}
                showDeleteAction={rulesHistory || rulesImportExport}
                readOnly={readOnly}
                rulesHistory={rulesHistory}
                rulesImportExport={rulesImportExport}
                setFlashAlert={setFlashAlert}
                setMenuOpen={setMenuOpen}
                setShowDeleteDialog2={setShowDeleteDialog}
              />
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      ) : (
        <div className="d-flex flex-items-center gap-2">
          <InsightsLink
            ruleset={ruleset}
            rulesetsUrl={resolvePath('')}
            readOnly={readOnly}
            rulesHistory={rulesHistory}
            rulesImportExport={rulesImportExport}
          />
        </div>
      )}
      {rulesetDeletionConfirmation && (
        <DangerConfirmationDialog
          isOpen={showDeleteDialog}
          title={`Delete ${rulesetOrPolicy}?`}
          text={`Are you sure you want to delete this ${rulesetOrPolicy}? This action cannot be undone.`}
          buttonText="Delete"
          onDismiss={() => {
            setShowDeleteDialog(false)
          }}
          onConfirm={() => {
            deleteRuleset(
              `${resolvePath('')}/${ruleset.id}`,
              resolvePath(''),
              setFlashAlert,
              setMenuOpen,
              setShowDeleteDialog,
              rulesetOrPolicy,
              navigate,
            )
          }}
        />
      )}
    </>
  )
}

export const RulesetList: FC<{
  rulesets: Ruleset[]
  sourceType: SourceType
  source: Repository | Organization
  readOnly?: boolean
  setFlashAlert: (flashAlert: FlashAlert) => void
}> = ({rulesets, sourceType, source, readOnly, setFlashAlert}) => {
  const {historyEnabled, importExportEnabled} = useRuleFeatures()
  const rulesHistory = historyEnabled
  const rulesImportExport = importExportEnabled

  const {rulesetPreviewCounts} = useTargetCount(source, sourceType, rulesets)

  const rulesetListHelper = (ruleset: Ruleset) => (
    <RulesetListHelper
      ruleset={ruleset}
      sourceType={sourceType}
      readOnly={readOnly}
      setFlashAlert={setFlashAlert}
      rulesetTargetCount={rulesetPreviewCounts[ruleset.id]}
    />
  )

  return (
    <ul>
      {rulesets.map((ruleset: Ruleset) => (
        <li key={ruleset.id} className="Box-row d-flex pr-px-3 py-2 flex-justify-between flex-items-center">
          {rulesHistory || rulesImportExport ? (
            <div className={`d-flex flex-row width-full`}>{rulesetListHelper(ruleset)}</div>
          ) : (
            rulesetListHelper(ruleset)
          )}
        </li>
      ))}
    </ul>
  )
}

const Subtext: FC<{ruleset: Ruleset; sourceType: SourceType; targetCount: number | undefined}> = ({
  ruleset,
  sourceType,
  targetCount,
}) => {
  const {ruleOrPolicy, rulesOrPolicies} = useRuleStrings()

  const ruleString = pluralize(
    ruleset.rules.length,
    `${humanize(ruleset.target)} ${ruleOrPolicy}`,
    `${humanize(ruleset.target)} ${rulesOrPolicies}`,
  )

  let targetString
  if (targetCount === undefined) {
    targetString = undefined
  } else if (sourceType === 'enterprise') {
    targetString = pluralize(targetCount, 'organization', 'organizations')
  } else if (sourceType === 'organization') {
    targetString = pluralize(targetCount, 'repository', 'repositories')
  } else if (sourceType === 'repository') {
    targetString =
      ruleset.target !== 'push'
        ? pluralize(targetCount, humanize(ruleset.target), PLURAL_RULESET_TARGETS)
        : 'all pushes'
  }

  return <span>{`${ruleString}${targetString ? ' • targeting ' : ''}${targetString || ''}`}</span>
}

const MissingConditionAlert: FC<{
  ruleset: Ruleset
}> = ({ruleset}) => {
  if (ruleset.target === 'push') {
    return null
  }

  if (ruleset.missingConditionTargets.length > 0 && ruleset.enforcement === RulesetEnforcement.Enabled) {
    let message = ''
    const firstMissing = ruleset.missingConditionTargets[0]!
    if (firstMissing === 'ref') {
      message = `${capitalize(ruleset.target)} conditions missing`
    } else {
      message = `${capitalize(firstMissing)} conditions missing`
    }
    return (
      <div className="text-small color-fg-danger mt-1">
        <AlertIcon size="small" />
        <span> </span>
        <em>{message}</em>
      </div>
    )
  }

  return null
}
